import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { path, includes, without, append, uniqBy } from 'ramda';
import axios from 'axios';
import { useMutation } from '@apollo/react-hooks';
import clsx from 'clsx';

import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
// import { ExpandMore } from '@material-ui/icons';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CameraAltIcon from '@material-ui/icons/CameraAlt';

import productInfoIcon from 'assets/images/product-info-icon.png';
import correctExampleImg from 'assets/images/upload-image-autofill-correct-example.png';
import wrongExampleImg from 'assets/images/upload-image-autofill-wrong-example.png';

import { compressImage, convertTiffToPng } from 'components/fileuploader/ImageUploader2';
import { KeycloakContext } from 'components/Secured';
import InputLabel from 'components/inputfields/InputLabel';
import { mapChatGptDataToFill, removeNilProperty } from 'components/vision/tabs/chatGPT';
import TagLabel from 'components/TagLabel';

import restEndpoints from 'constants/restEndpoints';
import COLORS from 'constants/colors';
// @ts-ignore
import 'assets/fileuploader.css';

import { S3_SIGNED_REQUEST_MUTATION } from 'graphql/mutations';

import useViewport from 'hooks/useViewports';
import useSearchParams from 'hooks/useSearchParams';

import IProduct, { IRecognizeImage } from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import IProducer from 'models/producer';

import { encodeLastPathUrl } from 'utils/imgix';
import { isBlank } from 'utils/helpers-ts';
// import { getUrlParams, updateUrlParams } from 'utils/urlUtils';
import { resize128ImageWithCompression } from 'utils/images-ts';

import { isNewFood } from '../fields/category';
// import ImageTranscribe, { Accordion, AccordionDetails, AccordionSummary, TImageTranscriber } from './ImageTranscriber';
import UploadImageGuideLabels from './UploadImageGuideLabels';
import UploadImageGuideExample from './UploadImageGuideExample';
import CameraCapture from './CameraCapture';
import MagicUploadPreview from './MagicUploadPreview';
import { ResponseSnackBar } from 'components/snackbar/SnackBar';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';
import { useIsFieldRequired } from '../validation-context/ProductValidationContext';
import MissingFieldHint from '../validation-context/MissingFieldHint';

export const PDF_MIME = 'application/pdf';
export const CSV_MIME = 'text/csv';
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const isPdfFile = (file: File) => file.type === PDF_MIME;
export const isCsvFile = (file: File) => file.type === CSV_MIME;
export const isXlsxFile = (file: File) => file.type === XLSX_MIME;

export const ALLOWED_IMAGE_FILE_TYPE_LIST = ['image/jpeg', 'image/jpg', 'image/tiff', 'image/png', 'image/webp'];
export const ALLOWED_DOCUMENT_FILE_TYPE_LIST = [
  PDF_MIME,
  CSV_MIME,
  XLSX_MIME, // .xlsx
];

const LOADING_TEXTS = [
  'Tar emot bilden...',
  'Skannar produkten...',
  'Läser av synlig text...',
  'Identifierar produkttyp...',
  'Kategoriserar produkten...',
  'Identifierar språk...',
  'Extraherar produktnamn...',
  'Söker efter varumärke...',
  'Läser av storlek och mängd...',
  'Letar efter streckkod...',
  'Analyserar produktinformation...',
  'Läser produktspecifikationer...',
  'Identifierar produktegenskaper...',
  'Söker efter märkningar och certifieringar...',
  'Kontrollerar produktdetaljer...',
  'Letar efter ursprungsinformation...',
  'Granskar övrig information...',
  'Mappar data mot produktschema...',
  'Validerar extraherad information...',
  'Sammanställer produktdata...',
  'Slutför analysen...',
  'Färdigställer resultatet...',
] as const;

type TDisplayFile = {
  name?: string;
  url?: string;
  file?: File;
  recognized?: boolean;
};

type TFillFile = {
  file: File;
  recognized?: boolean;
}

interface FillProductByImageProps {
  rootCategory?: IFoodlaCategoryOption;
  producerData?: IProducer;
  isFood?: boolean;
  isCreatingNew?: boolean;
  isChangedBrand?: boolean;
  state: IProduct;
  setState: (state: IProduct) => void;
  onChangedState: () => void;
  autofillFiles: TFillFile[];
  setAutofillFiles: React.Dispatch<React.SetStateAction<TFillFile[]>>
  fillImagesToBeRemoved: TDisplayFile[];
  setFillImagesToBeRemoved: (images: TDisplayFile[]) => void;
  approve?: boolean;
}

const grid = 4;

export const getImageStyle = ({
  recognized,
  isTobeRemoved,
}: {
  recognized?: boolean;
  isTobeRemoved?: boolean;
}): React.CSSProperties => ({
  display: 'inline-flex',
  borderRadius: 2,
  height: 120,
  boxSizing: 'border-box',
  cursor: 'pointer',
  opacity: recognized ? 0.3 : 1,
  filter: isTobeRemoved ? 'blur(6px)' : undefined,
});

export const getItemStyle = (): React.CSSProperties => ({
  maxHeight: 400,
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 ${grid}px 0 0`,
  borderRadius: 2,
  display: 'flex',
  border: 'none',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: 'white',
});

export type TSignedRequest = {
  signedRequest: string;
  url: string;
  pictureUrl: string;
  picturePath: string;
  fileName: string;
};

export type TSignedRequestResponse = {
  getS3SignRequest: {
    requests: TSignedRequest[];
  };
};

type TUploadedImageData = {
  url: string;
  name: string;
  pendingAutofill?: boolean;
};

const API_TIMEOUT = 60 * 1000;

interface IRecognizeImageData extends Partial<IProduct> {
  recognizeData?: boolean;
  model?: string;
}

interface IRecognizeCategoryData {
  categoryID?: string;
  categoryName?: string;
  categoryTree?: string;
  text?: string;
}

interface IRecognizeDimensionsData {
  gross_depth: number;
  gross_height: number;
  gross_width: number;
  gross_weight_num: number;
}

// One click of Magic Upload: its three requests send the same id, so the run log groups them; the
// product (when it is saved already) and the page go with it. Kept in magicUploadHistory too.
export type TMagicUploadContext = { uploadId: string; productId?: string | null };

export const newUploadId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // Not a secure context: fall back below.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const appendUploadContext = (formData: FormData, context?: TMagicUploadContext) => {
  if (!context) return;
  formData.append('uploadId', context.uploadId);
  formData.append('source', 'product-form');
  if (context.productId) formData.append('productId', context.productId);
};

const buildImageUrlWithFileName = (imageData: TUploadedImageData) => {
  const url = new URL(imageData.url);
  if (imageData.name) {
    url.searchParams.set('fileName', imageData.name);
  }
  return url.toString();
}

const recognizeImage = async (token: string, imagesData: TUploadedImageData[], args?: { isSuperuser?: boolean; model?: string; context?: TMagicUploadContext }): Promise<{ data: IRecognizeImageData, trimmedFileNames: string[] } | undefined> => {
  try {
    if (isBlank(token) || !imagesData.length) {
      return;
    }

    const formData = new FormData();
    imagesData.forEach(imageData => {
      formData.append('imageUrls', buildImageUrlWithFileName(imageData));
    });

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      if ((['pro', 'flash', 'fallback', 'cache'].includes(key) && ['1', 'true'].includes(value)) || key === 'temperature') {
        formData.append(key, value);
      }
    });

    if (args?.isSuperuser && args?.model) {
      formData.append('model', args.model);
    }
    appendUploadContext(formData, args?.context);

    const { data } = await axios.post<{ data: IRecognizeImageData, trimmedFileNames: string[] }>(restEndpoints.aiImage, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = data?.data;
    return { data: result, trimmedFileNames: data.trimmedFileNames };
  } catch (error: any) {
    console.error('Product info recognition error:', error);
    return;
  }
};

// const recognizeImageTranscriber = async (token: string, imageUrls: string[]) => {
//   try {
//     if (isBlank(token) || !imageUrls.length) {
//       return [];
//     }

//     const formData = new FormData();
//     imageUrls.forEach((imageUrl) => {
//       formData.append('imageUrls', imageUrl);
//     });

//     const { data } = await axios.post<{ data: TImageTranscriber[] }>(restEndpoints.aiImageTranscriber, formData, {
//       headers: { Authorization: `Bearer ${token}` },
//     });
//     const result = data?.data || [];
//     return result;
//   } catch (error: any) {
//     console.error('Image transcriber error:', error);
//     return [];
//   }
// };

const recognizeCategory = async (token: string, imagesData: TUploadedImageData[], context?: TMagicUploadContext) => {
  try {
    if (isBlank(token) || !imagesData.length) {
      return;
    }

    const formData = new FormData();
    imagesData.forEach(imageData => {
      formData.append('imageUrls', buildImageUrlWithFileName(imageData));
    });

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      if ((['pro', 'flash', 'fallback', 'cache'].includes(key) && ['1', 'true'].includes(value)) || key === 'temperature') {
        formData.append(key, value);
      }
    });

    appendUploadContext(formData, context);

    const { data } = await axios.post<{ data: IRecognizeCategoryData }>(restEndpoints.aiCategory, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = data?.data;
    return result;
  } catch (error: any) {
    console.error('Category recognition error:', error);
    return;
  }
};

const recognizeDimensions = async (token: string, imagesData: TUploadedImageData[], context?: TMagicUploadContext) => {
  try {
    if (isBlank(token) || !imagesData.length) {
      return;
    }

    const formData = new FormData();
    imagesData.forEach(imageData => {
      formData.append('imageUrls', buildImageUrlWithFileName(imageData));
    });

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      if ((['pro', 'flash', 'fallback', 'cache'].includes(key) && ['1', 'true'].includes(value)) || key === 'temperature') {
        formData.append(key, value);
      }
    });

    appendUploadContext(formData, context);

    const { data } = await axios.post<{ data: IRecognizeDimensionsData }>(restEndpoints.aiDimensions, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = data?.data;
    return result;
  } catch (error: any) {
    console.error('Dimensions recognition error:', error);
    return;
  }
};

export const useUploadRecognizeImages = ({ state }: { state: IProduct }) => {
  const keepRef = useRef({ state });
  keepRef.current.state = state;

  const [s3SignedRequest] = useMutation<TSignedRequestResponse>(S3_SIGNED_REQUEST_MUTATION, { refetchQueries: [] });

  const onUploadRecognizeImages = async (params: {
    autofillFiles: TFillFile[];
    fillImagesToBeRemoved: TDisplayFile[];
    onUploaded?: (data: IRecognizeImage) => void;
  }): Promise<TUploadedImageData[]> => {
    const { autofillFiles, fillImagesToBeRemoved, onUploaded } = params;

    const newFiles = autofillFiles.filter(
      item => !item.recognized && !fillImagesToBeRemoved.some(file => file.file === item.file)
    );
    if (!newFiles.length) return [];

    const s3SignedRequestInput = {
      isProduct: true,
      ean: keepRef.current.state?.EAN || '',
      producerName: state?.producerName || '',
      producerUsername: state?.producerUsername || '',
      files: newFiles.map(({ file }) => ({ fileName: file.name, fileType: file.type })),
      renameFiles: true,
      isCloudinaryForMagicUpload: true,
    };
    const { data: signedRequestData, errors: signedRequestErrors } = await s3SignedRequest({
      variables: {
        input: s3SignedRequestInput,
      },
    });

    let signedRequests: { file: File; signedRequest: TSignedRequest }[] = [];
    if (signedRequestErrors) {
      console.error('Get Signed Request error:', signedRequestErrors);
    } else {
      const requests = signedRequestData?.getS3SignRequest?.requests || [];
      signedRequests = requests.map((signedRequest, index) => {
        return { signedRequest, file: newFiles[index]?.file };
      });
    }

    let imageUrls = await Promise.all(
      signedRequests.map(async ({ signedRequest, file }) => {
        if (!signedRequest || !file) return null;

        let newImageUrl = '';

        try {
          await axios.put(signedRequest.signedRequest, file, {
            timeout: API_TIMEOUT,
            headers: { 'Content-Type': file.type },
          });
          const url = encodeLastPathUrl(signedRequest.pictureUrl);
          if (url) {
            onUploaded?.({ url, name: file.name, pendingAutofill: true });
            newImageUrl = url;
          }
        } catch (error) {
          console.error('Error uploading to S3:', error);
        }

        return {
          url: newImageUrl,
          name: file.name,
          pendingAutofill: true,
        };
        // const { w: imageWidthParams } = getUrlParams(newImageUrl);
        // if (imageWidthParams && Number(imageWidthParams) < 1024) {
        // } else {
        //   return updateUrlParams(newImageUrl, { w: 1024 });
        // }
      })
    );
    console.log('!!!imageUrls', imageUrls);

    // Filter out any empty URLs
    return imageUrls.filter(image => !!image?.url) as TUploadedImageData[];
  };

  return {
    onUploadRecognizeImages,
  };
}

const FillProductByImage: FC<FillProductByImageProps> = ({
  rootCategory,
  producerData,
  isFood,
  isCreatingNew,
  isChangedBrand,
  state,
  setState,
  onChangedState,
  autofillFiles,
  setAutofillFiles,
  fillImagesToBeRemoved,
  setFillImagesToBeRemoved,
  approve,
}) => {
  const isRecognizeImagesRequired = useIsFieldRequired('recognizeImages');
  const { searchParams } = useSearchParams();
  const keycloakCtx = useContext(KeycloakContext);
  const token = path(['keycloak', 'token'], keycloakCtx) as string;
  const isStore = keycloakCtx.keycloak.hasResourceRole('store');
  const isProducer = keycloakCtx.keycloak.hasResourceRole('producer');
  const isSuperuser = keycloakCtx.keycloak.hasResourceRole('superuser') as boolean;
  const isApoteaProducer = (isProducer || isStore) && producerData?.organizations?.includes('apotea');
  const isApoteaProductProducer = !!state?.producerOrganizations?.includes('apotea');
  const { smUp } = useViewport();

  const keepRef = useRef({ state, images: [] as IRecognizeImage[] });
  keepRef.current.state = state;

  const [convertingImage, setConvertingImage] = useState(false);
  const [loadingRecognize, setLoadingRecognize] = useState(false);
  const [isError, setIsError] = useState(false);
  const [modifiedCount, setModifiedCount] = useState(0);
  const [isModifiedDimensions, setIsModifiedDimensions] = useState(false);
  const [images, setImages] = useState<IRecognizeImage[]>(state.recognizeImages || []);
  keepRef.current.images = images;
  // const [imageTranscribers, setImageTranscribers] = useState<TImageTranscriber[]>(state.recognizedTexts || []);
  const [isRecognizedImage, setIsRecognizedImage] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [fileSizeErrors, setFileSizeErrors] = useState<string[]>([]);
  const [fileWarnings, setFileWarnings] = useState<string[]>([]);
  const [snackBarWarningAlert, setSnackBarWarningAlert] = useState('');

  useEffect(() => {
    if (!loadingRecognize) {
      setLoadingTextIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingTextIndex(prev => {
        if (prev >= LOADING_TEXTS.length - 1) {
          return prev; // Stop at last message
        }
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [loadingRecognize]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: [...ALLOWED_IMAGE_FILE_TYPE_LIST, ...ALLOWED_DOCUMENT_FILE_TYPE_LIST].join(', '),
    multiple: true,
    disabled: convertingImage || loadingRecognize,
    onDrop: async (acceptedFilesParams: File[]) => {
      const newFileSizeErrors: string[] = [];
      const acceptedFiles: File[] = [];

      acceptedFilesParams.forEach(file => {
        const isImage = /\.(jpe?g|png|webp|tiff?)$/i.test(file.name);
        if (isImage || file.size <= 40 * 1024 * 1024) {
          acceptedFiles.push(file);
        } else {
          newFileSizeErrors.push(`Filen "${file.name}" är för stor (max 40MB)`);
        }
      });
      setFileSizeErrors(newFileSizeErrors);

      if (!acceptedFiles.length) return;

      const hasCsvOrXlsx = acceptedFiles.some(file => isCsvFile(file) || isXlsxFile(file));

      if (hasCsvOrXlsx) {
        setSnackBarWarningAlert('Obs: Endast de första 100 raderna i CSV/XLSX-filer läses in vid autofyll');
      }

      setIsError(false);
      setConvertingImage(true);
      setIsRecognizedImage(false);
      setModifiedCount(0);

      let files = await Promise.all(
        acceptedFiles.map(async rawFile => {
          if (isCsvFile(rawFile) || isXlsxFile(rawFile) || isPdfFile(rawFile)) {
            return rawFile;
          }
          let file = await convertTiffToPng(rawFile);
          if (!file) return;

          file = await compressImage(file, 10);
          return file;
        })
      );

      files = files.filter(file => !!file);

      if (files.length) {
        setAutofillFiles(oldState => [...oldState, ...files.map(file => ({ file: file!, recognized: false }))]);
      }

      setConvertingImage(false);
      setFileWarnings([]);
    },
  });

  const { onUploadRecognizeImages } = useUploadRecognizeImages({ state });

  const pendingAutofillImages = useMemo(() => {
    return (state.recognizeImages || []).filter(item => item.pendingAutofill);
  }, [state.recognizeImages]);

  const displayFiles = useMemo(() => {
    const list: TDisplayFile[] = [];
    (state.recognizeImages || []).forEach(({ name, url, pendingAutofill }) => {
      list.push({ name, url, recognized: !pendingAutofill });
    });
    autofillFiles.forEach(({ file, recognized }) => {
      list.push({ name: file.name, url: URL.createObjectURL(file), recognized, file });
    });

    return list;
  }, [state.recognizeImages, autofillFiles]);

  const handleToggleDeleteFile = (file: TDisplayFile) => {
    if (includes(file, fillImagesToBeRemoved)) {
      setFillImagesToBeRemoved(without([file], fillImagesToBeRemoved));
    } else {
      const list = append(file, fillImagesToBeRemoved);
      setFillImagesToBeRemoved(uniqBy(i => i.url, list));
    }
  };

  const handleRecognizeCategory = async (imagesData: TUploadedImageData[], context: TMagicUploadContext) => {
    let isFoodAfterUpdate = isFood;

    if (isBlank(keepRef.current.state?.foodlaCategory?.id)) {
      const recognizeCategoryData = await recognizeCategory(token, imagesData, context);
      if (!isBlank(recognizeCategoryData?.categoryID) && !isBlank(recognizeCategoryData?.categoryName)) {
        const category = {
          id: recognizeCategoryData!.categoryID!,
          name: recognizeCategoryData!.categoryName!,
        };
        isFoodAfterUpdate = isNewFood(category, rootCategory);
        setState({ ...keepRef.current.state, foodlaCategory: category });
      }
    }

    return { isFoodAfterUpdate };
  };

  const handleRecognizeImagesAndCategory = async (imagesData: TUploadedImageData[]) => {
    const context: TMagicUploadContext = { uploadId: newUploadId(), productId: keepRef.current.state?.id || null };
    const [
      recognizeImageResponse,
      recognizeDimensionsData,
      { isFoodAfterUpdate },
    ] = await Promise.all([
      recognizeImage(token, imagesData, { isSuperuser, model: searchParams.model, context }),
      recognizeDimensions(token, imagesData, context),
      handleRecognizeCategory(imagesData, context),
    ]);
    const recognizeImageData = recognizeImageResponse?.data;
    const trimmedFileNames = recognizeImageResponse?.trimmedFileNames;
    setFileWarnings((trimmedFileNames || []).map(name => `Endast de första 100 raderna inlästa från ${name}`));

    const hasDimensionData = !!(
      recognizeDimensionsData?.gross_depth ||
      recognizeDimensionsData?.gross_height ||
      recognizeDimensionsData?.gross_width ||
      recognizeDimensionsData?.gross_weight_num
    );

    if (recognizeImageData?.recognizeData || hasDimensionData) {
      let { data: updatingState, modifiedCount: count } = mapChatGptDataToFill(
        keepRef.current.state,
        { ...recognizeImageData, ...recognizeDimensionsData },
        {
          isFood: isFoodAfterUpdate,
          isChangedBrand,
          producer: producerData,
          isCreatingNew,
          rootCategory,
          isSuperuser,
          isApoteaProductProducer,
          isApoteaProducer,
        }
      );
      updatingState = removeNilProperty(updatingState);
      if (count) {
        onChangedState();
      }
      updatingState.magicUploadHistory = [
        ...(keepRef.current.state.magicUploadHistory || []),
        {
          timestamp: new Date().toISOString(),
          imageUrls: imagesData.map(({ url }) => url),
          rawOutput: { ...recognizeImageData, dimensions: recognizeDimensionsData },
          model: recognizeImageData?.model || searchParams.model || 'gemini',
          uploadId: context.uploadId,
        },
      ].slice(-10);
      setState({
        ...keepRef.current.state,
        recognizeImages: keepRef.current.images.map(({ pendingAutofill, ...item }) => item),
        ...updatingState,
      });
      setAutofillFiles([]);
      setIsError(false);
      setModifiedCount(count);
    } else {
      setIsError(true);
      setModifiedCount(0);
    }

    setIsModifiedDimensions(hasDimensionData);
    setIsRecognizedImage(true);
  };

  // const handleRecognizeImageTranscribers = async (imageUrls: string[]) => {
  //   const recognizeImageTranscribers = await recognizeImageTranscriber(token, imageUrls);
  //   setImageTranscribers(recognizeImageTranscribers.filter( i => !!i.all_text_and_digits));
  // }

  const handleRunAI = async () => {
    setLoadingRecognize(true);
    setIsRecognizedImage(false);

    let imagesData = await onUploadRecognizeImages({
      autofillFiles,
      fillImagesToBeRemoved,
      onUploaded: (newImage) => setImages(oldImages => [...oldImages, newImage])
    });
    // includes pending autofill images which user just uploaded before
    imagesData = [...imagesData, ...pendingAutofillImages];

    if (imagesData.length === 0) {
      setIsError(true);
      setLoadingRecognize(false);
      return;
    }

    await Promise.all([
      handleRecognizeImagesAndCategory(imagesData),
      // handleRecognizeImageTranscribers(imageUrls),
    ]);

    setLoadingRecognize(false);
  };

  // useEffect(() => {
  //   if (imageTranscribers.length) {
  //     const recognizedTexts = imageTranscribers.map((imageTranscriber) => {
  //       const newTranscribed = {...imageTranscriber};
  //       if (newTranscribed && typeof newTranscribed.confidence_score === 'string') {
  //         newTranscribed.confidence_score = parseFloat(newTranscribed.confidence_score);
  //       }
  //       return newTranscribed;
  //     });
  //     setState({ ...state, recognizedTexts });
  //   }
  // }, [imageTranscribers]);

  const disabled =
    loadingRecognize || (
      !autofillFiles.some(item => !item.recognized && !fillImagesToBeRemoved.some(file => file.file === item.file)) &&
      !pendingAutofillImages.some(item => !fillImagesToBeRemoved.some(({ url }) => url === item.url))
    );

  return (
    <Box my={2} id="recognizeImages">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridGap: 16 }}>
        <InputLabel heading="Bilder & dokument med produktinformation" required={isRecognizeImagesRequired} />
        {approve && state?.approve?.find(x => x.field === 'recognizeImages') && (
          <ThreeWaySwitch state={state} setState={setState} name="recognizeImages" />
        )}
      </Box>
      <MissingFieldHint field="recognizeImages" />
      <p>
        Ladda upp bilder eller dokument med produktinformation, t.ex. produktens baksida, en etikett, artwork eller ett produktblad, så kan Sygrid autofylla fält, kategorisera och föreslå en beskrivning. Allt du laddar upp sparas på produkten som dokumentation, även utan autofyll.
      </p>
      {(isApoteaProducer || isApoteaProductProducer) && (
        <p>
          <b>Apotea kräver alltid</b> att komplett artwork eller bilder på förpackningens alla sidor laddas upp här. Vid behov kan du komplettera med produktdokument (t.ex. produktblad eller specifikation), så fyller Sygrid i logistik- och produktdata automatiskt.
        </p>
      )}
      {(isStore || isProducer) && (
        <UploadImageGuideLabels
          title="Riktlinjer för produktinformation"
          labels={['Skarp & tydlig text', 'Hela etiketten synlig', 'Rak vinkel', 'Inga reflektioner eller skuggor', 'PDF & Excel fungerar också']}
        />
      )}
      <div
        {...getRootProps({
          className: clsx(
            'dropzone-custom dropzone-secondary fill-product-by-image-bg',
            (!!autofillFiles.length || !!pendingAutofillImages.length) && 'has-item',
          ),
          style: { minHeight: !autofillFiles.length && !pendingAutofillImages.length ? '160px' : undefined },
        })}
      >
        <input {...getInputProps()} />
        <div className={clsx('dropzone-text', (convertingImage || loadingRecognize) && 'dropzone-loading')}>
          {convertingImage || loadingRecognize ? (
            <span className="loader">
              <CircularProgress size={30} />
              {loadingRecognize && (
                <span style={{ marginTop: 8, maxWidth: 500, fontSize: 16, fontWeight: 500 }}>
                  {isRecognizedImage
                    ? 'Sygrid har fyllt i informationen från bilden - nu analyserar vi bildkvaliteten för att säkerställa att allt stämmer.'
                    : LOADING_TEXTS[loadingTextIndex]}
                </span>
              )}
            </span>
          ) : (
            <>
              <div className="dropzone-icon">
                <img src={productInfoIcon} alt="" />
              </div>
              {!images?.length && (
                <div>
                  {smUp
                    ? 'Dra och släpp en bild eller ett dokument här, eller klicka för att välja från din dator (Stödda format: JPEG, PNG, TIFF, PDF, CSV, XLSX).'
                    : 'Klicka här för att öppna kameran eller välja en bild eller fil från ditt bibliotek'}
                </div>
              )}
            </>
          )}
        </div>

        {!!displayFiles.length && (
          <div style={{ maxWidth: '100%', overflow: 'auto' }}>
            <div className="list-items">
              {displayFiles.map(displayFile => {
                const isTobeRemoved = includes(displayFile, fillImagesToBeRemoved);
                const { url, name, recognized, file } = displayFile;
                let thumbnailUrl;
                if (!isBlank(url)) {
                  thumbnailUrl = resize128ImageWithCompression(url);
                }

                // 1. Check if it's a local image (using MIME type first, fallback to extension)
                const isLocalImage = file?.type
                  ? file.type.startsWith('image/')
                  : name && /\.(jpe?g|png|webp|tiff?)$/i.test(name);

                // 2. Render image if it's from the server (!file) OR if it's a confirmed local image
                const shouldRenderImage = (!file || isLocalImage) && !url?.includes('/raw/');

                return (
                  <div style={getItemStyle()} onClick={event => event.stopPropagation()}>
                    <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                      {shouldRenderImage ? (
                        <img
                          src={thumbnailUrl}
                          style={getImageStyle({ recognized, isTobeRemoved })}
                          alt="Not available"
                          onClick={() => window.open(url, '_blank')}
                        />
                      ) : (
                        <div
                          style={{
                            ...getImageStyle({ recognized, isTobeRemoved }),
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '12px',
                            boxSizing: 'border-box',
                          }}
                          onClick={() => window.open(url, '_blank')}
                          title={`Open ${name}`}
                        >
                          <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                            <b style={{ fontSize: '24px' }}>
                              {name?.includes('.') ? name.split('.').pop()?.toUpperCase() : 'FILE'}
                            </b>
                          </div>
                        </div>
                      )}
                    </div>
                    <b>{name}</b>
                    {isTobeRemoved && (
                      <TagLabel
                        type="error"
                        icon={<InfoOutlinedIcon fontSize="small" />}
                        label="Uppdatera/spara produkten för att ta bort bilden"
                        style={{ flexDirection: 'column', maxWidth: 150, margin: '5px 0' }}
                      />
                    )}
                    <Button
                      fullWidth
                      size="small"
                      style={{ color: '#B71C1C', marginTop: 4 }}
                      onClick={() => handleToggleDeleteFile(displayFile)}
                    >
                      {!isTobeRemoved && <DeleteOutlineIcon fontSize="small" />}
                      <span style={{ marginLeft: 4, marginTop: 2 }}>{isTobeRemoved ? 'Ångra' : 'Ta bort'}</span>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {!smUp && (
        <Button
          fullWidth
          type="button"
          variant="outlined"
          color="primary"
          style={{ marginTop: 4, marginBottom: 12 }}
          onClick={() => setIsCameraOpen(true)}
          disabled={convertingImage || loadingRecognize}
          startIcon={<CameraAltIcon />}
        >
          <span>TA BILDER MED KAMERA</span>
        </Button>
      )}

      <Button
        fullWidth
        disabled={disabled}
        type="button"
        variant="contained"
        color="primary"
        style={{ backgroundColor: !disabled ? COLORS.mainGreen : undefined, marginBottom: 8 }}
        onClick={handleRunAI}
      >
        <span>AUTOFYLL PRODUkTDATA</span>
      </Button>

      {!!modifiedCount && (
        <Box id="ProductForm-FillProductByImage-Message-Success" mt={1} color="green" fontWeight={700}>
          Sygrid har hittat produktinformation i det du laddade upp och vi har hjälpt dig att fylla i fälten nedan!
        </Box>
      )}

      {!!isModifiedDimensions && (
        <Box id="ProductForm-FillDimensionsByImage-Message-Success" mt={1} color="green" fontWeight={700}>
          Sygrid har föreslagit dimensioner utifrån dina bilder. Granska och justera vid behov.
        </Box>
      )}

      {isError && (
        <Box id="ProductForm-FillProductByImage-Message-Error" mt={1} color="orangered" fontWeight={700}>
          Vi kunde inte hitta produktinformation i det du laddade upp. Testa gärna med en tydligare bild eller ett
          dokument där produktinformationen är synlig så gör vi ett försök till!
        </Box>
      )}
      {!!fileWarnings.length && (
        <Box id="ProductForm-FillProductByImage-Message-Errors" mt={1} color="orange" fontWeight={700}>
          {fileWarnings.map((message, index) => (
            <Box key={`${message}_${index}`}>{message}</Box>
          ))}
        </Box>
      )}
      {!!fileSizeErrors.length && (
        <Box id="ProductForm-FillProductByImage-Message-Errors" mt={1} color="orangered" fontWeight={700}>
          {fileSizeErrors.map((error, index) => (
            <Box key={`${error}_${index}`}>{error}</Box>
          ))}
        </Box>
      )}

      {snackBarWarningAlert && (
        <ResponseSnackBar
          duration={5000}
          background='warn'
          horizontal='center'
          success={false}
          message={snackBarWarningAlert}
          onClosedCallback={() => setSnackBarWarningAlert('')}
        />
      )}

      {/* {imageTranscribers.length === 1 ? (
        <Box mt={2}>
          <ImageTranscribe data={imageTranscribers[0]} />
        </Box>
      ) : (
        imageTranscribers.map((imageTranscriber) => {
          return (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                {imageTranscriber.image_name}
              </AccordionSummary>
              <AccordionDetails borderless>
                <ImageTranscribe hideHeader data={imageTranscriber} />
              </AccordionDetails>
            </Accordion>
          );
        })
      )} */}

      {(isStore || isProducer) && (
        <UploadImageGuideExample
          tipTitle="Tips för bättre scanning av etiketter"
          tipList={[
            'Fotografera etiketten rakt framifrån, inte i vinkel',
            'Se till att belysningen är jämn utan reflexer',
            'Undvik skuggor som kan dölja text',
            'Ta bilden så nära att texten är tydligt läsbar (se till att texten inte är suddig)',
            'Hela etiketten måste vara med i bilden',
          ]}
          correctImageSrc={correctExampleImg}
          wrongImageSrc={wrongExampleImg}
        />
      )}

      {/* Camera Capture Component */}
      {
        <CameraCapture
          isOpen={isCameraOpen}
          onPhotosCapture={photos => {
            setAutofillFiles(oldState => [...oldState, ...photos.map(file => ({ file, recognized: false }))]);
          }}
          onCancel={() => setIsCameraOpen(false)}
        />
      }

      {isSuperuser && <MagicUploadPreview state={state} />}
    </Box>
  );
};

export default FillProductByImage;
